import type React from 'react';

import {PlainCellRenderer} from '../components/PlainCellRenderer/PlainCellRenderer';
import type {EagColumn} from '../types/EagColumn';
import type {RowData} from '../types/RowData';
import {CELL_RENDERER_REGISTRY} from './cellRendererRegistry';
import {LineHeightToPx} from './lineHeightToValue';
import {toPlainLabel} from './toPlainLabel';

// thumbnailHeightPx is pre-computed in GridCell as rowHeightPx - verticalPaddingPx
// so ThumbnailCellRenderer receives the exact image height it should render at,
// without needing any store subscription of its own.
// Defaults to LineHeightToPx.DEFAULT for callers that don't render thumbnails
// (drag overlay, tests).
// isWrap is computed once in GridCell (settings.valueWrapMode === 'WRAP' && isWrappableType)
// and threaded down so each renderer can adjust its layout for wrap vs. truncate.
// emptyCellValue is the placeholder renderers display for cells with no value —
// '' (nothing, the default) or '–' (en dash) — resolved from the emptyCellValueMode setting
// in GridRow. Callers that don't render real data cells (summary row, tests)
// omit it and keep the empty-renders-nothing behavior.
// eslint-disable-next-line max-params -- Pre-existing violation
export const renderCell = (
  column: EagColumn,
  value: unknown,
  row: RowData,
  thumbnailHeightPx: number = LineHeightToPx.DEFAULT,
  isWrap: boolean = false,
  emptyCellValue?: string
) => {
  const Renderer = CELL_RENDERER_REGISTRY[column.type as keyof typeof CELL_RENDERER_REGISTRY];

  if (!Renderer) {
    // .otherwise() fallback: handles PLAIN and any unknown types gracefully
    return (
      <PlainCellRenderer
        value={toPlainLabel(value)}
        column={column}
        row={row}
        isWrap={isWrap}
        emptyCellValue={emptyCellValue}
      />
    );
  }

  // eslint-disable-next-line -eslint/no-explicit-any
  const AnyRenderer = Renderer as React.ComponentType<any>;
  return (
    <AnyRenderer
      value={value}
      column={column}
      row={row}
      isWrap={isWrap}
      thumbnailHeightPx={thumbnailHeightPx}
      emptyCellValue={emptyCellValue}
    />
  );
};
